import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { HeaderNav } from "@/components/HeaderNav";
import { ReflectionContextStep } from "@/components/ReflectionContextStep";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const SCALE = [
  { v: 1, label: "Strongly disagree" }, { v: 2, label: "Disagree" }, { v: 3, label: "Neither agree nor disagree" },
  { v: 4, label: "Agree" }, { v: 5, label: "Strongly agree" },
];

export default function GlimpseQuestions() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [resp, setResp] = useState({});
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [context, setContext] = useState(null);
  const shownAt = useRef(Date.now());

  useEffect(() => {
    const load = async () => {
      try {
        let { data } = await api.get("/glimpse/current").catch(async (e) => {
          if (e.response?.status !== 404) throw e;
          await api.post("/glimpse/start");
          return api.get("/glimpse/current");
        });
        if (data.status === "completed") { navigate("/glimpse/portrait", { replace: true }); return; }
        setItems(data.items); setResp(data.responses || {});
        setContext(data.reflection_context || (Object.keys(data.responses || {}).length ? { legacy: true } : null));
        const first = data.items.findIndex((it) => !(it.id in (data.responses || {})));
        setIdx(first === -1 ? data.items.length - 1 : first);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
    };
    load();
  }, [navigate]);

  useEffect(() => { shownAt.current = Date.now(); }, [idx]);

  const answer = async (v) => {
    const it = items[idx];
    setResp((r) => ({ ...r, [it.id]: v }));
    api.post("/glimpse/responses", { responses: { [it.id]: v }, timings: { [it.id]: Date.now() - shownAt.current } })
      .catch(() => toast.error("Could not save that answer. Please try again."));
    if (idx < items.length - 1) setTimeout(() => setIdx((i) => Math.min(i + 1, items.length - 1)), 220);
  };

  const finish = async () => {
    setBusy(true);
    try { await api.post("/glimpse/submit"); navigate("/glimpse/portrait"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };

  if (loading) return <div><HeaderNav /><div className="max-w-2xl mx-auto px-5 pt-24 cg-eyebrow animate-pulse">Loading…</div></div>;
  if (!context) {
    const saveCtx = async (ctx) => {
      try { const { data } = await api.post("/glimpse/context", ctx); setContext(data.reflection_context); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    return <div><HeaderNav /><ReflectionContextStep onSave={saveCtx} eyebrow="Your Authority Glimpse" /></div>;
  }
  const it = items[idx];
  const answered = items.filter((x) => x.id in resp).length;
  const allDone = answered === items.length && items.length > 0;
  return (
    <div>
      <HeaderNav />
      <div className="max-w-2xl mx-auto px-5 sm:px-8 pt-12 pb-24" data-testid="glimpse-questions">
        <div className="flex items-center justify-between text-sm text-[#9CA3AF]">
          <span data-testid="glimpse-progress-label">Question {idx + 1} of {items.length}</span>
          <span>{answered} answered · saved automatically</span>
        </div>
        <div className="h-1 rounded-full bg-[#222634] mt-3 mb-10 overflow-hidden"><div className="h-full bg-brass transition-all duration-500" style={{ width: `${(answered / items.length) * 100}%` }} /></div>
        {it && (
          <div key={it.id} className="cg-fade-up">
            <p className="font-serif text-2xl sm:text-3xl text-[#F3F4F6] leading-snug min-h-[110px]" data-testid="glimpse-statement">{it.item_text}</p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-5 gap-2.5" role="radiogroup">
              {SCALE.map((s) => (
                <button key={s.v} role="radio" aria-checked={resp[it.id] === s.v} onClick={() => answer(s.v)} data-testid={`glimpse-option-${s.v}`}
                  className={`rounded-xl border px-3 py-3.5 text-sm transition-colors ${resp[it.id] === s.v ? "border-brass bg-[rgba(217,162,95,0.12)] text-[#F3F4F6]" : "border-[var(--cg-border)] text-[#9CA3AF] hover:border-brass/60"}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="mt-10 flex items-center justify-between gap-3">
          <button data-testid="glimpse-back" onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[var(--cg-border)] text-[#E8EAEE] disabled:opacity-30"><ArrowLeft className="w-4 h-4" /> Back</button>
          {allDone ? (
            <button data-testid="glimpse-submit" onClick={finish} disabled={busy}
              className="inline-flex items-center gap-2 bg-brass text-obsidian font-medium px-6 py-3 rounded-full cg-scale-btn hover:bg-amber2 disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} See My Glimpse Portrait <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button data-testid="glimpse-next" onClick={() => setIdx((i) => Math.min(items.length - 1, i + 1))} disabled={!it || !(it.id in resp) || idx === items.length - 1}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[var(--cg-border)] text-[#E8EAEE] disabled:opacity-30">Next <ArrowRight className="w-4 h-4" /></button>
          )}
        </div>
      </div>
    </div>
  );
}
